import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, EmptyState, Skeleton } from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/shared/lib/format';
import { auditControllerListAuditLogsV1 } from '@/api/generated/audit-logs/audit-logs';
import type { AuditLogDto } from '@/api/generated/model';
import { auditLogKeys } from '@/shared/lib/auditLogKeys';

export interface AuditLogPanelProps {
  /** What the entries are about — `player_onboarding`, `player_offboarding`. */
  subjectType: string;
  subjectId: string;
  /**
   * Where the labels live, e.g. `onboarding.audit`. The panel reads
   * `<prefix>.title`, `<prefix>.empty` and `<prefix>.action.<action>` from it.
   */
  i18nPrefix: string;
}

/**
 * Everything that has happened to one record, newest first.
 *
 * Lives in `shared` rather than in a module because onboarding and offboarding
 * both pin it below their screens, and a second copy is how the two would drift
 * apart. It is parameterised by subject rather than by feature, which is also
 * the shape the audit API already has.
 *
 * Pinned to the bottom of a screen rather than hidden behind a tab of its own,
 * because its value is contextual: the question it answers is "who changed this
 * and when", asked while looking at the thing that changed.
 *
 * Entries render from a known set of action keys with a fallback to the raw
 * action string. A new backend action therefore shows up as something
 * readable-ish rather than a blank row, and the missing translation is obvious
 * enough to get fixed.
 */
export function AuditLogPanel({ subjectType, subjectId, i18nPrefix }: AuditLogPanelProps) {
  const { t } = useTranslation();

  // Keyed by subject rather than by feature, so two panels on the same screen
  // never share a cache entry.
  const audit = useQuery({
    queryKey: auditLogKeys.forSubject(subjectType, subjectId),
    queryFn: ({ signal }) =>
      auditControllerListAuditLogsV1({ subjectType, subjectId, limit: 50 }, { signal }),
    enabled: Boolean(subjectId),
  });

  return (
    <Card className="gap-2">
      <CardHeader>
        <CardTitle className="text-[11px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
          {t(`${i18nPrefix}.title`)}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {audit.isPending ? (
          <div className="grid gap-3 py-2">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/3" />
          </div>
        ) : audit.data && audit.data.data.length > 0 ? (
          <ul className="divide-y divide-border/60">
            {audit.data.data.map((entry: AuditLogDto) => {
              const details = describe(entry);
              return (
                <li
                  key={entry.id}
                  className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 py-2.5"
                >
                  <div className="grid min-w-0 gap-0.5">
                    <span className="text-sm font-medium">
                      {t(`${i18nPrefix}.action.${entry.action}`, { defaultValue: entry.action })}
                    </span>
                    {details ? (
                      <span className="text-xs wrap-break-word text-muted-foreground">
                        {details}
                      </span>
                    ) : null}
                  </div>
                  <span className="text-xs whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatDateTime(entry.createdAt)} · {entry.actorName}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState description={t(`${i18nPrefix}.empty`)} className="py-4" />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * A one-line summary of an entry's metadata.
 *
 * The shape differs per action and is deliberately untyped on the wire — a JSON
 * blob whose contents are the action's business. Rather than a switch over
 * every action, this renders whatever keys are present, which keeps working as
 * actions are added.
 */
function describe(entry: AuditLogDto): string {
  const metadata = entry.metadata as Record<string, unknown> | null | undefined;
  if (!metadata) return '';

  return Object.entries(metadata)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) =>
      Array.isArray(value) ? `${key}: ${value.join(', ')}` : `${key}: ${String(value)}`,
    )
    .join(' · ');
}
